// F7: OS-level notifications behind the `desktop` pref. In the Tauri shell this is
// tauri-plugin-notification (via desktop.ts); in a browser it is the Web Notifications API.
// Mobile shells are out of scope (no APNs/FCM in F7).
import {
  isDesktop,
  isMobileShell,
  notificationPermissionGranted,
  requestNotificationPermission,
  sendOsNotification,
} from "../desktop";

export type OsPermission = "granted" | "denied" | "prompt" | "unsupported";

const DENIED_KEY = "grackle.osNotifyDenied";

function inDesktopShell(): boolean {
  return isDesktop() && !isMobileShell();
}

export function osNotificationsSupported(): boolean {
  return inDesktopShell() || (typeof Notification !== "undefined" && !isDesktop());
}

export async function getOsPermission(): Promise<OsPermission> {
  if (inDesktopShell()) {
    try {
      if (await notificationPermissionGranted()) return "granted";
    } catch {
      return "unsupported";
    }
    return localStorage.getItem(DENIED_KEY) === "1" ? "denied" : "prompt";
  }
  if (typeof Notification === "undefined" || isDesktop()) return "unsupported";
  return Notification.permission === "default" ? "prompt" : Notification.permission;
}

export async function requestOsPermission(): Promise<OsPermission> {
  if (inDesktopShell()) {
    const res = await requestNotificationPermission();
    if (res === "granted") {
      localStorage.removeItem(DENIED_KEY);
      return "granted";
    }
    if (res === "denied") localStorage.setItem(DENIED_KEY, "1");
    return res === "denied" ? "denied" : "prompt";
  }
  if (typeof Notification === "undefined" || isDesktop()) return "unsupported";
  const res = await Notification.requestPermission();
  return res === "default" ? "prompt" : res;
}

/** Shows one OS notification. `onClick` only fires for the browser path: the desktop plugin
 * has no click event, so the desktop caller reopens the link on the next window focus. */
export async function showOsNotification(title: string, body: string, onClick?: () => void): Promise<void> {
  if (inDesktopShell()) {
    await sendOsNotification(title, body);
    return;
  }
  if (typeof Notification === "undefined") return;
  const n = new Notification(title, { body });
  n.onclick = () => {
    window.focus();
    onClick?.();
    n.close();
  };
}

/** Whether a push should become an OS notification right now: the user's `desktop` pref for
 * this kind is on, permission is granted, and the app is not the focused window. Pure so the
 * rule is testable without a window. */
export function shouldShowOsNotification(opts: {
  prefEnabled: boolean;
  permission: OsPermission;
  focused: boolean;
}): boolean {
  return opts.prefEnabled && opts.permission === "granted" && !opts.focused;
}
